import { useMemo, useState, useEffect } from "react";
import { TruckWithShifts } from "./types";
import { getDriverName, travelPathToGpsPoints } from "../lib/utils";
import { useLazyGetTruckTravelPathQuery } from "../../../../shared/api/truck/truck.grpc";
import { useListPickupLocationsQuery } from "../../../../shared/api/pickup-locations/pickup-locations.grpc";
import { useListDestinationLocationsQuery } from "~/shared/api/locations/locations.grpc";
import { useListRoutesQuery } from "../../../../shared/api/routes/route.grpc";
import { useDateRangeUrl } from "./useDateRangeUrl";
import { useSelectedCompany } from "../../../../shared/store/company/hooks/useSelectedCompany";
import { ConnectProto } from "@racoon/api";
import { toTimestamp, fromTimestamp, normalizeForSearch } from "@racoon/lib";
import { buildStepPins, numberRouteJobs } from "~/features/map-view/model/helpers/step-pin-items";
export interface UseGpsTrackingDataOptions {
  searchQuery?: string;
  selectedVehicleId?: string;
}

export function useGpsTrackingData(options: UseGpsTrackingDataOptions) {
  const { searchQuery = "", selectedVehicleId } = options;

  // Get organization ID from the store
  const { selectedCompanyId } = useSelectedCompany();

  const { start, end, setDateRange } = useDateRangeUrl();

  // Fetch pickup locations and depots
  const { data: { pickupLocations = [] } = {} } = useListPickupLocationsQuery(
    { serviceProviderIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const { data: { locations: allDepots = [] } = {} } = useListDestinationLocationsQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      types: [
        ConnectProto.LocationType.DEPOT,
        ConnectProto.LocationType.DISPOSAL_SITE,
        ConnectProto.LocationType.GAS_STATION,
        ConnectProto.LocationType.PROCESSING_PLANT,
        ConnectProto.LocationType.SORTING_COMPLEX,
        ConnectProto.LocationType.TRANSFER_STATION,
      ],
    },
    { skip: !selectedCompanyId },
  );

  const { data: { routes: listedRoutes = [] } = {} } = useListRoutesQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      datetimeFilter: {
        from: start ? toTimestamp(new Date(start)) : undefined,
        to: end ? toTimestamp(new Date(end)) : undefined,
      },
    },
    { skip: !selectedCompanyId },
  );

  // A shift that was never started is not shown at all, nor are its routes.
  const allRoutes = useMemo(
    () => listedRoutes.filter((route) => route.driverOnShift?.startedAt),
    [listedRoutes],
  );

  // Build vehicle map with processes
  const vehicles = useMemo(() => {
    const map = new Map<bigint, TruckWithShifts>();
    const workedShiftIds = new Set<bigint>();
    allRoutes.forEach((item: ConnectProto.Route) => {
      const shift = item.driverOnShift;
      const truck = shift?.truck;
      const id = truck?.id;

      if (!id || !shift || !truck) return;

      if (item.status !== ConnectProto.RouteStatus.CANCELLED) workedShiftIds.add(shift.id);

      const existing = map.get(id);

      if (!existing) {
        map.set(id, {
          ...truck,
          shifts: [shift],
          updatedAt: item.updatedAt,
        });
        return;
      }

      if (!existing.shifts.some((s) => s.id === shift.id)) {
        existing.shifts.push(shift);
      }

      // Keep the freshest updatedAt across all routes of this truck
      const existingAt = fromTimestamp(existing.updatedAt);
      const incomingAt = fromTimestamp(item.updatedAt);
      if (incomingAt && (!existingAt || incomingAt > existingAt)) {
        existing.updatedAt = item.updatedAt;
      }
    });
    const trucks = Array.from(map.values());
    trucks.forEach((truck) => {
      truck.shifts.sort(
        (a, b) =>
          (fromTimestamp(a.startedAt)?.getTime() ?? 0) -
          (fromTimestamp(b.startedAt)?.getTime() ?? 0),
      );
      truck.cancelledShiftIds = new Set(
        truck.shifts.filter((s) => !workedShiftIds.has(s.id)).map((s) => s.id),
      );
    });
    return trucks;
  }, [allRoutes]);

  // Filter vehicles and find used locations
  const { filteredVehicles, usedPickupLocations, stepPins, usedDepots } = useMemo(() => {
    // Filter vehicles
    let filtered = vehicles;
    if (searchQuery) {
      const q = normalizeForSearch(searchQuery);
      filtered = filtered.filter(
        (v) =>
          normalizeForSearch(v.plateNumber).includes(q) ||
          normalizeForSearch(v.model).includes(q) ||
          v.shifts.some((s) => normalizeForSearch(getDriverName(s)).includes(q)),
      );
    }

    // Find used locations
    const locIds = new Set<bigint>();
    const depotIds = new Set<bigint>();

    const procs = selectedVehicleId
      ? allRoutes.filter((p) => String(p.driverOnShift?.truck?.id) === selectedVehicleId)
      : allRoutes;

    procs.forEach((p) => {
      p.jobs?.forEach((job) => {
        const pickupLocationId = job?.pickupLocationId;
        if (pickupLocationId) locIds.add(pickupLocationId);
      });
      const depotLocationId = p?.destinationLocationId;
      if (depotLocationId) depotIds.add(depotLocationId);
    });

    // One truck's day reads as one count across its trips; several trucks' counts would clash.
    const stepPins = selectedVehicleId ? buildStepPins(numberRouteJobs(procs, "day")) : undefined;

    return {
      filteredVehicles: filtered,
      usedPickupLocations: pickupLocations.filter((l) => locIds.has(l.id)),
      stepPins,
      usedDepots: allDepots.filter((d) => depotIds.has(d.id)),
    };
  }, [vehicles, searchQuery, selectedVehicleId, allRoutes, pickupLocations, allDepots]);

  // Get travel path for all visible trucks via useLazyGetTruckTravelPathQuery
  const [triggerTravelPath] = useLazyGetTruckTravelPathQuery();

  // Save travel path data for all trucks
  const [allTravelPaths, setAllTravelPaths] = useState<Record<string, any>>({});

  useEffect(() => {
    let isMounted = true;
    async function fetchSelected() {
      if (!selectedVehicleId || !selectedCompanyId) {
        setAllTravelPaths({});
        return;
      }
      try {
        // Parse the id straight from the string: int64 ids can exceed
        // Number.MAX_SAFE_INTEGER, so going through Number() could corrupt it.
        const truckId = BigInt(selectedVehicleId);
        const res = await triggerTravelPath(
          {
            id: truckId,
            ownerOrganizationId: BigInt(selectedCompanyId),
            fromTo: {
              from: toTimestamp(new Date(start)),
              to: toTimestamp(new Date(end)),
            },
          },
          true,
        ).unwrap();
        if (isMounted) setAllTravelPaths({ [selectedVehicleId]: res?.geojsonFeature ?? null });
      } catch {
        if (isMounted) setAllTravelPaths({ [selectedVehicleId]: null });
      }
    }
    fetchSelected();
    return () => {
      isMounted = false;
    };
  }, [selectedVehicleId, start, end, selectedCompanyId, triggerTravelPath]);

  // Collect gpsPoints from allTravelPaths
  const gpsPoints = useMemo(() => {
    if (!selectedVehicleId) return [];
    const vehicle = vehicles.find((v) => String(v.id) === selectedVehicleId);
    if (!vehicle) return [];
    // Driver names across every shift this truck appeared in
    const names = Array.from(new Set(vehicle.shifts.map(getDriverName).filter(Boolean)));
    return travelPathToGpsPoints(
      allTravelPaths[selectedVehicleId],
      vehicle,
      names.length ? names : ["Unknown Driver"],
    );
  }, [allTravelPaths, vehicles, selectedVehicleId]);

  // Filter routes for the selected vehicle
  const vehicleRoutes = useMemo(() => {
    if (!selectedVehicleId) return [];

    return allRoutes.filter((route) => {
      const rTruckId = route.driverOnShift?.truckId;
      return rTruckId != null && String(rTruckId) === selectedVehicleId;
    });
  }, [allRoutes, selectedVehicleId]);

  // Get selected vehicle
  const selectedVehicle = useMemo(
    () =>
      selectedVehicleId ? (vehicles.find((v) => String(v.id) === selectedVehicleId) ?? null) : null,
    [vehicles, selectedVehicleId],
  );

  return {
    vehicles: filteredVehicles,
    gpsPoints,
    routes: vehicleRoutes,
    selectedVehicle,
    usedPickupLocations,
    stepPins,
    usedDepots,
    start,
    end,
    setDateRange,
  };
}
